import { mutationOptions } from '@tanstack/react-query';
import { accountKeys } from './accountQueries';
import type { TRPC } from './types';
import type { QueryClient } from '@tanstack/react-query';
import type { InfopankMonitoredCompanies } from '@/apiTypes/types';

export const monitoringKeys = {
  subscriptionStatus: (trpc: TRPC) => trpc.monitoring.subscriptionStatus.pathKey(),
  subscribe: (trpc: TRPC) => trpc.monitoring.subscribe.mutationKey(),
  unsubscribe: (trpc: TRPC) => trpc.monitoring.unsubscribe.mutationKey(),
};

export const subscriptionStatusQueryOptions = ({ trpc, externalId }: { trpc: TRPC; externalId: string }) =>
  trpc.monitoring.subscriptionStatus.queryOptions(
    { externalId },
    {
      staleTime: 30 * 1000,
    },
  );

type MutationDeps = {
  trpc: TRPC;
  queryClient: QueryClient;
  onSuccess?: () => void;
};

type OptimisticContext = {
  previous: [readonly unknown[], InfopankMonitoredCompanies[] | undefined][];
};

const invalidateSubscriptionData = (trpc: TRPC, queryClient: QueryClient) => {
  void queryClient.invalidateQueries({ queryKey: accountKeys.monitoredCompanies(trpc) });
  void queryClient.invalidateQueries({ queryKey: accountKeys.monitoredEvents(trpc) });
};

const rollback = (queryClient: QueryClient, context?: OptimisticContext) => {
  context?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
};

export const subscribeMutationOptions = ({ trpc, queryClient, onSuccess }: MutationDeps) => {
  const listKey = accountKeys.monitoredCompanies(trpc);
  return mutationOptions({
    ...trpc.monitoring.subscribe.mutationOptions(),
    onMutate: async (variables): Promise<OptimisticContext> => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueriesData<InfopankMonitoredCompanies[]>({ queryKey: listKey });
      queryClient.setQueriesData<InfopankMonitoredCompanies[]>({ queryKey: listKey }, (old) => {
        const current = old ?? [];
        if (current.some((company) => String(company.companyId) === variables.externalId)) {
          return current;
        }
        return [
          {
            companyId: Number(variables.externalId),
            name: variables.name || '',
            status: null,
            statusDescription: null,
          },
          ...current,
        ];
      });
      return { previous };
    },
    onError: (_error, _variables, context) => rollback(queryClient, context),
    onSuccess: () => onSuccess?.(),
    onSettled: () => invalidateSubscriptionData(trpc, queryClient),
  });
};

export const unsubscribeMutationOptions = ({ trpc, queryClient, onSuccess }: MutationDeps) => {
  const listKey = accountKeys.monitoredCompanies(trpc);
  return mutationOptions({
    ...trpc.monitoring.unsubscribe.mutationOptions(),
    onMutate: async (variables): Promise<OptimisticContext> => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueriesData<InfopankMonitoredCompanies[]>({ queryKey: listKey });
      queryClient.setQueriesData<InfopankMonitoredCompanies[]>({ queryKey: listKey }, (old) =>
        (old ?? []).filter((company) => String(company.companyId) !== variables.externalId),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => rollback(queryClient, context),
    onSuccess: () => onSuccess?.(),
    onSettled: () => invalidateSubscriptionData(trpc, queryClient),
  });
};
